<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import __MACROS_useVModel from "/vue-macros/define-models/use-vmodel";
import { onMounted } from 'vue';
import { TagDto, TagService } from '@/api';
import Tag from '@/components/tags/Tag.vue';
import { VAutocomplete } from 'vuetify/lib';

interface TagSelectorProps {
  filterMode?: boolean;
  returnObject?: boolean;
}
const props = defineProps({
  filterMode: { type: Boolean, default: false },
  returnObject: { type: Boolean, default: true },
  value: null
});

type TagSelectorEmits = {
  (event: 'input', tags: TagDto[] | string[]): void
}
const emits = defineEmits(["input"]);

const { value: selectedTags } = __MACROS_useVModel(["value", undefined, "input"]);

let fetchedTags = _ref<TagDto[]>([]);
let loading = _ref(false);
let searchString = _ref('');
const tagSelector = _ref<{ blur:() => void } | null>(null);

const mustReturnObject = _computed(() => (props.filterMode ? false : props.returnObject !== false));
const isFilterMode = _computed(() => props.filterMode);
const innerIcon = _computed(() => (props.filterMode ? 'icon-tag' : ''));

/**
 * Get different item icon based on content value
 * @param hasValue true if not empty
 */
const customItemIcon = (hasValue: boolean) => (hasValue ? 'icon-check-square-fill' : 'icon-check-square-outline-empty');

/**
 * Close v-autocomplete dropdown
 */
const closeMenu = () => {
  tagSelector.value?.blur();
};

/**
 * Fetch complete tag data
 */
 const fetchTags = async () => {
  loading.value = true;
  const { data } = await TagService.getAllTag({ itemsPerPage: -1 });
  fetchedTags.value = data;
  loading.value = false;
};

/**
 * Clear selected tags & fetched data
 */
 const clear = () => {
  selectedTags.value = [];
  searchString.value = '';
  emits('input', selectedTags.value);
  closeMenu();
};

/**
 * Helper function for v-autocomplete item-value
 * @param item tag item
 */
const getItemValue = (item: TagDto): TagDto | string => (isFilterMode.value ? item.id : item);

/**
 * Check if tooltip should be shown for given item
 * @param item tag item name
 */
 const shouldShowTooltip = (item: string) => item.length >= 20;

/**
 * Computes display values for the selected tags based on filter mode
 * @returns a list of tag IDs (strings) or tag objects (TagDto) based on current selection and filter mode
 */
const displayTags = _computed<(string | TagDto)[]>({
  get: () => {
    const tags = selectedTags.value;

    if (!tags) return [];

    if (isFilterMode.value && tags.every((t): t is string => typeof t === 'string')) {
      return tags;
    }

    if (!isFilterMode.value && tags.every((t): t is TagDto => typeof t === 'object')) {
      return tags.map(getItemValue);
    }

    return [];
  },
  set: (newValues: (string | TagDto)[]) => {
    if (!newValues) return;

    if (isFilterMode.value) {
      selectedTags.value = newValues
        .map((id) => fetchedTags.value.find((tag) => tag.id === id))
        .filter((tag): tag is TagDto => !!tag);
    } else {
      selectedTags.value = newValues as TagDto[];
    }
  },
});

onMounted(() => {
  fetchTags();
});
</script>

<template lang="pug">
v-autocomplete(
  class="tag-selector"
  ref="tagSelector"
  v-bind='$attrs'
  v-model="displayTags"
  v-on='$listeners'
  :loading="loading"
  :items="fetchedTags"
  :search-input.sync="searchString"
  :return-object="mustReturnObject"
  :prepend-inner-icon="innerIcon"
  @change="searchString=''"
  @blur="searchString=''"
  @click:clear="clear"
  :placeholder="$t('tags.placeholder')"
  label=""
  item-value="id"
  item-text="name"
  data-track="tag-selector"
  dense
  outlined
  hide-details
  hide-no-data
  multiple
  clearable
)

  template(v-slot:item="{ item, attrs }")
    div(data-track="tag-selector-item")
      v-icon.ml-2.mr-1 {{ customItemIcon(attrs.inputValue) }}
      Tag.my-3( :tag="item" :key="item.id" small) {{ item.name }}

  template(v-slot:selection="{ item }")
    Tag.my-1( v-if="!isFilterMode" :tag="item" :key="item.id" small) {{ item.name }}
    v-tooltip( v-else-if="shouldShowTooltip(item.name)" top )
      template(v-slot:activator='{ on }')
        Tag.mb-1( v-on="on" :tag="item" :key="item.id" small style="max-width: 165px !important") {{ item.name }}
      span {{ item.name }}
      div.tooltip-arrow-bottom
    Tag.my-1( v-else :tag="item" :key="item.id" small style="max-width: 165px !important") {{ item.name }})

</template>

<style lang="scss" scoped>
.resetLineHeight {
  line-height: initial;
}

.tag-selector {
  .v-select__slot {
    padding: 3px 0px 3px;
  }

  .v-input__append-inner {
    margin-top: 5px !important;
  }
}
</style>
